import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Chip, CircularProgress, Collapse, IconButton, Tab, Table, TableBody, TableCell,
    TableHead, TableRow, Tabs, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { gridDimensionsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';

// ────────────────────────────────────────────────────────────────────────
// Expandable row of the Data tab's reservations table (SoftTouch users,
// 2026-10-07): everything the webshop stored for one order — the JSON blobs
// the grid doesn't show (customer, addresses, store, products, pricing,
// vouchers) plus the technical exchange with the POS (request, response,
// supplier request, raw order payload).
//
// Compact on purpose (most admin users work on a laptop): tabs Overview /
// Products / Technical, the overview cards show a few readable lines and
// open "all fields" / JSON only on request.
//
// Fetched on expand through GET /reservations/{id}/details (mini-api), which
// decodes the blobs and masks credential-like keys. The shapes are the
// storefront's own (OrderController); this panel never assumes a field
// exists — unknown fields still show in the generic views.
// ────────────────────────────────────────────────────────────────────────

// per webshop+reservation, so re-expanding a row doesn't refetch
const _cache = new Map();

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isEmpty = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length) || (isPlainObject(v) && !Object.keys(v).length);

const fmt = (v) => {
    if (v === true) return '✓';
    if (v === false) return '—';
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
};
const money = (v) => {
    const n = Number(v);
    return v === null || v === undefined || v === '' || Number.isNaN(n) ? '' : `€ ${n.toFixed(2).replace('.', ',')}`;
};
const join = (parts, sep = ' ') => parts.filter(p => p !== null && p !== undefined && String(p).trim() !== '').join(sep);

// Customer/address fields in a readable order; anything else follows.
const CUSTOMER_ORDER = [
    'firstname', 'name', 'email', 'phone_number', 'address', 'number', 'box', 'zip', 'city', 'country',
    'company', 'vat', 'vat_number', 'is_b2b', 'birthday', 'language', 'id', 'customer_id',
];
const PRODUCT_COLUMNS = [
    'barcode', 'article', 'description', 'name', 'brand', 'color', 'size',
    'quantity', 'quantityReservation', 'orderQtyAtSupplier',
    'price', 'discounted_price', 'discount_percentage', 'total',
];

const orderedEntries = (obj, order) => {
    const keys = Object.keys(obj);
    const head = order.filter(k => keys.includes(k));
    return [...head, ...keys.filter(k => !head.includes(k))].map(k => [k, obj[k]]);
};

// A person / address as (at most) three readable lines:
// name (company) · street nr, zip city, country · email · phone
const addressLines = (o) => {
    if (!isPlainObject(o)) return [];
    const street = join([join([o.address || o.street, o.number]), o.box ? `bus ${o.box}` : '']);
    return [
        join([join([o.firstname, o.name || o.lastname]), o.company ? `(${o.company})` : '', o.vat || o.vat_number]),
        join([street, join([o.zip, o.city]), o.country], ', '),
        join([o.email, o.phone_number || o.phone], ' · '),
    ].filter(l => l && String(l).trim() !== '');
};

const CopyJson = ({ value, tr }) => {
    const [done, setDone] = useState(false);
    return (
        <Tooltip title={done ? tr('cfg_res_det_copied', 'Copied') : tr('cfg_res_det_copy', 'Copy as JSON')}>
            <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => {
                e.stopPropagation();
                try {
                    navigator.clipboard.writeText(JSON.stringify(value, null, 2));
                    setDone(true);
                    setTimeout(() => setDone(false), 1500);
                } catch (_) { /* clipboard blocked: nothing to do */ }
            }}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
        </Tooltip>
    );
};
CopyJson.propTypes = { value: PropTypes.any, tr: PropTypes.func.isRequired };

const JsonBlock = ({ value, maxHeight = 240 }) => (
    <Box component="pre" sx={{
        m: 0, p: 1, maxHeight, overflow: 'auto', fontSize: 11.5, lineHeight: 1.4,
        borderRadius: 1, bgcolor: 'action.hover', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}>
        {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
    </Box>
);
JsonBlock.propTypes = { value: PropTypes.any, maxHeight: PropTypes.number };

// key → value list. A nested object (one level) becomes a group: its key as
// a full-width subheading, its fields as rows of the SAME grid — a grid inside
// the value cell had its own label column, so the labels jumped sideways.
// Deeper levels show as JSON.
const KeyValues = ({ obj, order = [], groupLabels = {} }) => {
    const entries = orderedEntries(obj, order).filter(([, v]) => !isEmpty(v));
    if (!entries.length) return <Typography variant="caption" color="text.secondary">—</Typography>;
    const label = (k, indent) => (
        <Typography component="dt" sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'monospace', lineHeight: 1.6, pl: indent ? 1 : 0 }}>{k}</Typography>
    );
    const value = (v) => (
        <Typography component="dd" sx={{ m: 0, minWidth: 0, fontSize: 12.5, lineHeight: 1.6, wordBreak: 'break-word' }}>{fmt(v)}</Typography>
    );
    return (
        <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(90px, max-content) 1fr', columnGap: 1, rowGap: 0 }}>
            {entries.map(([k, v], i) => (isPlainObject(v) ? (
                <React.Fragment key={k}>
                    <Typography sx={{ gridColumn: '1 / -1', fontSize: 11.5, fontWeight: 700, mt: i ? 0.5 : 0 }}>{groupLabels[k] || k}</Typography>
                    {orderedEntries(v, order).filter(([, x]) => !isEmpty(x)).map(([k2, v2]) => (
                        <React.Fragment key={k2}>{label(k2, true)}{value(v2)}</React.Fragment>
                    ))}
                </React.Fragment>
            ) : (
                <React.Fragment key={k}>{label(k, false)}{value(v)}</React.Fragment>
            )))}
        </Box>
    );
};
KeyValues.propTypes = { obj: PropTypes.object.isRequired, order: PropTypes.array, groupLabels: PropTypes.object };

// Compact card: a few readable lines; "Alle velden" → key/values, "JSON" → raw.
const Card = ({ id, title, value, lines, tr, order = CUSTOMER_ORDER, defaultView = 'short', groupLabels, children }) => {
    const [view, setView] = useState(defaultView);
    if (isEmpty(value)) return null;
    const hasShort = !!children || (Array.isArray(lines) && lines.length > 0);
    const mode = hasShort ? view : (view === 'short' ? (isPlainObject(value) ? 'all' : 'json') : view);
    const linkSx = (active) => ({
        fontSize: 11, cursor: 'pointer', color: active ? 'text.primary' : 'text.secondary',
        fontWeight: active ? 700 : 400, userSelect: 'none', '&:hover': { color: 'primary.main' },
    });
    return (
        <Box data-res-card={id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1.25, px: 1.25, py: 0.75, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, flex: 1 }}>{title}</Typography>
                {hasShort && <Typography component="span" sx={linkSx(mode === 'short')} onClick={() => setView('short')}>{tr('cfg_res_det_short', 'Short')}</Typography>}
                {isPlainObject(value) && <Typography component="span" sx={linkSx(mode === 'all')} onClick={() => setView('all')}>{tr('cfg_res_det_all_fields', 'All fields')}</Typography>}
                <Typography component="span" sx={linkSx(mode === 'json')} onClick={() => setView('json')}>JSON</Typography>
                <CopyJson value={value} tr={tr} />
            </Box>
            {mode === 'short' && children}
            {mode === 'short' && !children && lines.map((l, i) => (
                <Typography key={i} sx={{ fontSize: 12.5, lineHeight: 1.5, wordBreak: 'break-word', fontWeight: i === 0 ? 600 : 400 }}>{l}</Typography>
            ))}
            {mode === 'all' && <KeyValues obj={value} order={order} groupLabels={groupLabels} />}
            {mode === 'json' && <JsonBlock value={value} maxHeight={200} />}
        </Box>
    );
};
Card.propTypes = {
    id: PropTypes.string,
    children: PropTypes.node,
    groupLabels: PropTypes.object,
    title: PropTypes.node.isRequired, value: PropTypes.any, lines: PropTypes.array,
    tr: PropTypes.func.isRequired, order: PropTypes.array, defaultView: PropTypes.oneOf(['short', 'all', 'json']),
};

const ProductsTable = ({ products }) => {
    const list = useMemo(() => (Array.isArray(products) ? products : Object.values(products || {})), [products]);
    const cols = useMemo(
        () => PRODUCT_COLUMNS.filter(c => list.some(p => isPlainObject(p) && !isEmpty(p[c]))),
        [list],
    );
    const [open, setOpen] = useState(null);
    if (!list.length) return null;
    return (
        <Box sx={{ overflowX: 'auto', maxHeight: 320, overflowY: 'auto' }}>
            <Table size="small" stickyHeader sx={{ '& td, & th': { py: 0.25, px: 0.75, fontSize: 12, whiteSpace: 'nowrap' } }}>
                <TableHead>
                    <TableRow>
                        <TableCell padding="none" />
                        {cols.map(c => <TableCell key={c} sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '11px !important' }}>{c}</TableCell>)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {list.map((p, i) => (
                        <React.Fragment key={i}>
                            <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(open === i ? null : i)}>
                                <TableCell padding="none">{open === i ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}</TableCell>
                                {cols.map(c => <TableCell key={c}>{fmt(isPlainObject(p) ? p[c] : '')}</TableCell>)}
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={cols.length + 1} sx={{ p: '0 !important', border: open === i ? undefined : 0 }}>
                                    <Collapse in={open === i} unmountOnExit>
                                        <Box sx={{ p: 0.75 }}><JsonBlock value={p} maxHeight={200} /></Box>
                                    </Collapse>
                                </TableCell>
                            </TableRow>
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};
ProductsTable.propTypes = { products: PropTypes.oneOfType([PropTypes.array, PropTypes.object]) };

// Articles as "2× Trui wol   M · Navy   € 79,90" lines (first few; the
// Products tab has the full table).
const ItemsSummary = ({ list, max = 4, onMore, moreLabel }) => (
    <Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto auto', columnGap: 1.25, rowGap: 0, alignItems: 'baseline' }}>
            {list.slice(0, max).map((it, i) => {
                const o = isPlainObject(it) ? it : {};
                return (
                    <React.Fragment key={i}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, textAlign: 'right' }}>{o.quantity ?? ''}×</Typography>
                        <Typography noWrap sx={{ fontSize: 12.5 }} title={o.description || o.name || o.article || ''}>{o.description || o.name || o.article || o.barcode || '—'}</Typography>
                        <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>{join([o.size, o.color], ', ')}</Typography>
                        <Typography sx={{ fontSize: 12.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(o.total ?? (Number(o.quantity) * Number(o.discounted_price ?? o.price)))}</Typography>
                    </React.Fragment>
                );
            })}
        </Box>
        {list.length > max && (
            <Typography component="span" onClick={onMore} sx={{ fontSize: 11.5, color: 'primary.main', cursor: 'pointer' }}>
                {moreLabel(list.length - max)}
            </Typography>
        )}
    </Box>
);
ItemsSummary.propTypes = { list: PropTypes.array.isRequired, max: PropTypes.number, onMore: PropTypes.func, moreLabel: PropTypes.func.isRequired };

// label … amount rows, amounts right-aligned
const Amounts = ({ rows }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', columnGap: 1, rowGap: 0 }}>
        {rows.map(([label, value, strong]) => (
            <React.Fragment key={label}>
                <Typography noWrap sx={{ fontSize: 12.5, fontWeight: strong ? 700 : 400 }}>{label}</Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: strong ? 700 : 400, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
            </React.Fragment>
        ))}
    </Box>
);
Amounts.propTypes = { rows: PropTypes.array.isRequired };

// The detail panel spans ALL columns (wider than the visible grid when it
// scrolls sideways): pin it to the visible width so nothing falls off-screen.
const VisibleWidth = ({ children }) => {
    const apiRef = useGridApiContext();
    const dims = useGridSelector(apiRef, gridDimensionsSelector);
    const width = dims?.viewportInnerSize?.width;
    return <Box sx={{ position: 'sticky', left: 0, width: width || '100%', boxSizing: 'border-box' }}>{children}</Box>;
};
VisibleWidth.propTypes = { children: PropTypes.node };

const ReservationDetailPanel = (props) => <VisibleWidth><ReservationDetailBody {...props} /></VisibleWidth>;

const ReservationDetailBody = ({ row, proxy, t, cacheKey }) => {
    const tr = (k, fb) => (typeof t === 'function' ? t(k, fb) : fb);
    const key = `${cacheKey}:${row.id}`;
    const [data, setData] = useState(() => _cache.get(key) || null);
    const [error, setError] = useState('');
    const [tab, setTab] = useState('overview');

    useEffect(() => {
        if (_cache.has(key)) { setData(_cache.get(key)); return undefined; }
        let alive = true;
        setError('');
        proxy('GET', `/reservations/${row.id}/details`)
            .then((r) => {
                if (!alive) return;
                if (r && r.success && r.data) {
                    _cache.set(key, r.data);
                    setData(r.data);
                } else {
                    // a 404 on the route itself = the shop still runs an older mini-api;
                    // the proxy's own text ("Mini-api returned an error") says nothing,
                    // so it only goes along as the technical detail
                    setError(r?.data?.body?.message || r?.message || '-');
                }
            })
            .catch((e) => { if (alive) setError(e?.message || '-'); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    if (error) {
        return (
            <Box sx={{ px: 2, py: 1 }}>
                <Alert severity="warning" sx={{ py: 0 }}>
                    {tr('cfg_res_det_failed', 'Could not load the details. Does the webshop run the latest mini-api?')}
                    {error !== '-' && <Typography variant="caption" component="div" sx={{ opacity: 0.75 }}>{error}</Typography>}
                </Alert>
            </Box>
        );
    }
    if (!data) return <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} /></Box>;

    const b = data.blobs || {};
    const r = data.row || {};
    const flags = [
        ['sent_reservation', tr('cfg_res_det_flag_pos', 'In the POS')],
        ['is_queued', tr('cfg_res_det_flag_queued', 'In the queue')],
        ['sent_email', tr('cfg_res_det_flag_mail', 'Mail sent')],
        ['sent_message', tr('cfg_res_det_flag_print', 'Print message sent')],
        ['payed_online', tr('cfg_res_det_flag_paid', 'Paid online')],
    ];
    const products = Array.isArray(b.products_data) ? b.products_data : (isPlainObject(b.products_data) ? Object.values(b.products_data) : []);
    const tech = [
        ['reservation_request_data', tr('cfg_res_det_pos_request', 'Request to the POS')],
        ['reservation_response', tr('cfg_res_det_pos_response', 'Response of the POS')],
        ['reservation_supplier_request_data', tr('cfg_res_det_supplier_request', 'Request to the supplier')],
        ['payload', tr('cfg_res_det_payload', 'Order request from the webshop')],
    ].filter(([k]) => !isEmpty(b[k]));
    const vouchers = [
        ['cheques_data', tr('cfg_res_det_cheques', 'Gift vouchers used')],
        ['cheques_order_data', tr('cfg_res_det_cheques_order', 'Gift vouchers ordered')],
    ].filter(([k]) => !isEmpty(b[k]));

    const p = isPlainObject(b.pricing_data) ? b.pricing_data : {};
    const priceRows = [
        Number(p.total_without_discount) && Number(p.total_discount) ? [tr('cfg_res_det_subtotal', 'Subtotal'), money(p.total_without_discount)] : null,
        Number(p.total_discount) ? [tr('cfg_res_det_discount', 'Discount'), `− ${money(p.total_discount)}`] : null,
        p.shipping !== undefined ? [join([tr('cfg_res_det_shipping', 'Shipping'), p.shipping_method ? `(${p.shipping_method})` : '']), money(p.shipping)] : null,
        p.total !== undefined ? [tr('cfg_res_det_total', 'Total'), money(p.total), true] : null,
    ].filter(Boolean);
    const s = isPlainObject(b.store_data) ? b.store_data : {};
    const storeLine = join([s.display_name || s.name, s.id ? `(${s.id})` : '']);
    // Delivery = where it goes (address) + which store handles it
    const deliveryValue = isEmpty(b.customer_delivery_data) && isEmpty(b.store_data)
        ? null
        : { ...(isPlainObject(b.customer_delivery_data) ? { address: b.customer_delivery_data } : {}), ...(isPlainObject(b.store_data) ? { store: b.store_data } : {}) };
    const deliveryLines = [
        ...addressLines(b.customer_delivery_data),
        storeLine ? `${tr('cfg_res_det_store', 'Store')}: ${storeLine}` : '',
    ].filter(Boolean);

    // [card, weight] — at lg+ the weights become the column widths, so the row
    // always fills the panel; narrower screens wrap to equal columns.
    const cards = [
        [<Card key="c" title={tr('cfg_res_det_customer', 'Customer')} id="customer_data" value={b.customer_data} lines={addressLines(b.customer_data)} tr={tr} />, 1, !isEmpty(b.customer_data)],
        [<Card key="d" title={tr('cfg_res_det_delivery_short', 'Delivery')} id="delivery" value={deliveryValue} lines={deliveryLines} tr={tr} order={CUSTOMER_ORDER}
            groupLabels={{ address: tr('cfg_res_det_delivery_address', 'Delivery address'), store: tr('cfg_res_det_store', 'Store') }} />, 1, !!deliveryValue],
        [<Card key="i" title={tr('cfg_res_det_invoice', 'Invoice address')} id="customer_invoice_data" value={b.customer_invoice_data} lines={addressLines(b.customer_invoice_data)} tr={tr} />, 1, !isEmpty(b.customer_invoice_data)],
        [<Card key="p" title={`${tr('cfg_res_det_items', 'Articles')} (${products.length})`} id="products_data" value={b.products_data} tr={tr} order={[]}>
            <ItemsSummary list={products} onMore={() => setTab('products')}
                moreLabel={(n) => (typeof t === 'function' ? t('cfg_res_det_more_items', { n, defaultValue: '+ {{n}} more' }) : `+ ${n} more`)} />
        </Card>, 2, products.length > 0],
        [<Card key="a" title={tr('cfg_res_det_payment', 'Payment')} id="pricing_data" value={b.pricing_data} tr={tr} order={[]}>
            <Amounts rows={priceRows} />
        </Card>, 0.9, priceRows.length > 0],
        ...vouchers.map(([k, title]) => [<Card key={k} id={k} title={title} value={b[k]} tr={tr} order={[]} />, 1, true]),
    ].filter(([, , show]) => show);
    const lgColumns = cards.map(([, w]) => `minmax(0, ${w}fr)`).join(' ');

    return (
        <Box sx={{ px: 1.5, pl: 6, pt: 0.5, pb: 1 }} data-res-detail={row.id}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 30, mr: 1, '& .MuiTab-root': { minHeight: 30, py: 0, px: 1.25, fontSize: 12.5, textTransform: 'none' } }}>
                    <Tab value="overview" label={tr('cfg_res_det_tab_overview', 'Overview')} />
                    <Tab value="products" label={`${tr('cfg_res_det_products', 'Products')} (${products.length})`} disabled={!products.length} />
                    <Tab value="technical" label={tr('cfg_res_det_tab_technical', 'Technical')} disabled={!tech.length} />
                </Tabs>
                {flags.map(([f, label]) => (
                    <Chip key={f} size="small" label={label} sx={{ height: 20, fontSize: 11 }}
                        color={Number(r[f]) === 1 ? 'success' : 'default'}
                        variant={Number(r[f]) === 1 ? 'filled' : 'outlined'} />
                ))}
                {r.pos_id && <Chip size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} label={`${tr('cfg_res_det_pos', 'POS')} ${r.pos_id}`} />}
                {r.payed_via && <Chip size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} label={`${r.payed_via}${r.payed_id ? ' · ' + r.payed_id : ''}`} />}
            </Box>

            <Box sx={{ mt: 0.75 }}>
                {tab === 'overview' && (
                    <>
                        {!isEmpty(r.remark) && (
                            <Typography sx={{ fontSize: 12.5, mb: 0.75, px: 1, py: 0.25, borderRadius: 1, bgcolor: 'action.hover' }}>
                                <strong>{tr('cfg_res_det_remark', 'Remark')}:</strong> {r.remark}
                            </Typography>
                        )}
                        <Box sx={{
                            display: 'grid', gap: 0.75, alignItems: 'stretch',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: lgColumns },
                        }}>
                            {cards.map(([card]) => card)}
                        </Box>
                    </>
                )}
                {tab === 'products' && (
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.25 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 0.5 }}><CopyJson value={b.products_data} tr={tr} /></Box>
                        <ProductsTable products={b.products_data} />
                    </Box>
                )}
                {tab === 'technical' && (
                    <>
                        <Box sx={{ display: 'grid', gap: 0.75, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' } }}>
                            {tech.map(([k, title]) => <Card key={k} id={k} title={title} value={b[k]} tr={tr} order={[]} defaultView="json" />)}
                        </Box>
                        {r.uid && <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>uid {r.uid}</Typography>}
                    </>
                )}
            </Box>
        </Box>
    );
};

ReservationDetailBody.propTypes = {
    row:      PropTypes.object.isRequired,
    proxy:    PropTypes.func.isRequired,
    t:        PropTypes.func,
    cacheKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
ReservationDetailPanel.propTypes = ReservationDetailBody.propTypes;

export default ReservationDetailPanel;
